import { inject, injectable } from "@launchtray/tsyringe-async";
import { Bindable } from "bindable-data";
import { NotifyProperty } from "bindable-data";
import { nameof } from "bindable-data";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { ITournamentModel } from "./ITournamentModel";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { TournamentState } from "./TournamentState";
import { Ticker } from "pixi.js";

@injectable()
export class TournamentModel extends Bindable implements ITournamentModel {
    private static readonly UPDATE_DELAY_MS = 300000;

    private readonly networkClient: INetworkClient;
    private readonly logger: ILogger;

    public readonly state: NotifyProperty<TournamentState>;
    public readonly time: NotifyProperty<number>;

    private updateTime: number = 0;

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.logger = logger;
        this.time = new NotifyProperty<number>(this, nameof(this, "time"), null);
        this.state = new NotifyProperty<TournamentState>(this, nameof(this, "state"), TournamentState.UNAVAILABLE);
        Ticker.shared.add(this.onTick, this);

        document.addEventListener("visibilitychange", () => {
            if (!document.hidden && this.state.value === TournamentState.UNAVAILABLE) {
                this.getTournamentsSettings();
            }
        });
    }

    public async getTournamentsSettings(): Promise<void> {
        try {
            this.updateTime = 0;

            const tournamentSettings = await this.networkClient.getTournamentsSettings();

            if (tournamentSettings == null || tournamentSettings.endsAt == null || tournamentSettings.endsAt == "") {
                this.state.setValue(TournamentState.UNAVAILABLE);
                return;
            }

            const startTime = new Date(tournamentSettings.startsAt).getTime();
            const endTime = new Date(tournamentSettings.endsAt).getTime();
            const currentTime = new Date().getTime();

            if (currentTime < startTime) {
                this.time.setValue(startTime - currentTime);
                this.state.setValue(TournamentState.PLANNED);
                return;
            }

            if (currentTime >= startTime && currentTime <= endTime) {
                this.time.setValue(endTime - currentTime);
                this.state.setValue(TournamentState.STARTED);
                return;
            }

            this.time.setValue(currentTime - endTime);
            this.state.setValue(TournamentState.FINISHED);
        } catch (error: unknown) {
            this.logger.error("Exception on get tournament settings: " + error);
        }
    }

    private async onTick(): Promise<void> {
        const timeToEnd = this.time.value;
        if (timeToEnd != null) {
            let diff = 0;
            if (this.state.value == TournamentState.FINISHED) {
                diff = Math.max(timeToEnd + Ticker.shared.deltaMS, 0);
            } else {
                diff = Math.max(timeToEnd - Ticker.shared.deltaMS, 0);
            }
            this.time.setValue(diff);
            if (diff === 0) {
                this.updateTime = Ticker.shared.deltaMS;
                await this.getTournamentsSettings();
                return;
            }
        }

        this.updateTime = Math.min(this.updateTime + Ticker.shared.deltaMS, TournamentModel.UPDATE_DELAY_MS);
        if (this.updateTime === TournamentModel.UPDATE_DELAY_MS) {
            await this.getTournamentsSettings();
        }
    }
}
